// src/services/apiEndpoints.ts

import { useConfigStore } from '@/config/config.store';
import { getConfig } from '@/config';

const config = getConfig();

export function getBaseUrl(domain: string): string {
  const isMswEnabled = useConfigStore.getState().isMswEnabled[domain];
  return isMswEnabled ? config.VITE_MSW_BASE_URL : config.VITE_API_BASE_URL;
}

export const API_ENDPOINTS = {
  AUTH_LOGIN: `/auth/login`,
  AUTH_TOKEN: `/auth/token`,
  AUTH_REFRESH_TOKEN: `/auth/refresh`,
  AUTH_RESET_PASSWORD: `/auth/reset-password`,
  AUTH_CREATE_PASSWORD: `/auth/create-password`,
  AUTH_OTP: `/auth/otp`,
  AUTH_CHANGE_PASSWORD: `/auth/change-password`,
  AUTH_LOGOUT: `/auth/logout`,
  USER_PROFILE: `/user/me`,
  USER_CUSTOMERS: `/user/customers`,
  CUSTOMER_CURRENCIES: `/customer/:customer_uuid/currencies`,
  CUSTOMER_FILTER_SOURCES: `/source-filter-options/:customer_uuid`,
  CUSTOMER_USERS: `/customer/:customer_uuid/users`,
  CUSTOMER_USER: `/customer/user/:customer_user_uuid`,
  CUSTOMER_USER_CREATE: `/customer/:customer_uuid/user`,
  CUSTOMER_USER_REINVITE: `/customer/user/:customer_user_uuid/resend-invite`,
  SOURCES_COMMERCIAL_TERMS: `/source/commercial-terms/:customer_uuid`,
  FUNDING_COMMISSIONS: `/funding/commissions/:customer_uuid`,
  FUNDING_ADVANCE_DETAILS: `/funding/:type/:customer_uuid/advance-details/:advance_id`,
  FUNDING_COMMISSIONS_FILTERS: `/funding/commissions/:customer_uuid/filters`,
  FUNDING_COMMISSIONS_DOWNLOAD_ALL: `/funding/commissions/:customer_uuid/download`,
  FUNDING_REVENUES: `/funding/revenues/:customer_uuid`,
  FUNDING_REVENUES_DOWNLOAD_ALL: `/funding/revenues/:customer_uuid/download`,
  FUNDING_REVENUES_FILTERS: `/funding/revenues/:customer_uuid/filters`,
  FUNDING_INVOICES_SUMMARY: `/funding/advances/:customer_uuid/summary`,
  FUNDING_INVOICES_DETAILED: `/funding/advances/:customer_uuid/detailed`,
  FUNDING_INVOICES_COLLECTIONS: `/funding/advances/:customer_uuid/collections-details/:invoice_uuid`,
  FUNDING_INVOICES_ISSUED: `/funding/advances/:customer_uuid/invoice-details/:invoice_uuid`,
  FUNDING_INVOICES_SUMMARY_DOWNLOAD: `/funding/advances/:customer_uuid/summary/download`,
  FUNDING_INVOICES_DETAILED_DOWNLOAD: `/funding/advances/:customer_uuid/detailed/download`,
  FUNDING_DOCUMENTS: `/funding/documents/:customer_uuid`,
  FUNDING_DOCUMENTS_DOWNLOAD: `/funding/documents/:customer_uuid/download/:document_uuid`,
  FUNDING_ACTIVITY_CASH_MOVEMENTS: `/funding/transactions/:customer_uuid/cash-movements`,
  FUNDING_ACTIVITY_ADVANCE_MOVEMENTS: `/funding/transactions/:customer_uuid/advance-movements`,
  FUNDING_ACTIVITY_CASH_MOVEMENTS_DOWNLOAD: `/funding/transactions/:customer_uuid/cash-movements/download`,
  FUNDING_ACTIVITY_ADVANCE_MOVEMENTS_DOWNLOAD: `/funding/transactions/:customer_uuid/advance-movements/download`,
  FUNDING_ACTIVITY_CASH_MOVEMENTS_DOWNLOAD_PDF: `/funding/transactions/:customer_uuid/cash-movements/:transaction_uuid/download`,
  FUNDING_ACTIVITY_CHARGE_DETAILS: `/funding/transactions/:customer_uuid/charges/:charge_uuid`,
  FUNDING_CAMPAIGNS_DETAILS: `/funding/campaigns/:customer_uuid/campaign-details/:campaign_uuid`,
  DASHBOARD_AVAILABLE_FUNDS: `/dashboard/available-funds/:customer_uuid`,
  DASHBOARD_TOP_OUTSTANDING_INVOICES: `/dashboard/top-advances/:customer_uuid`,
  DASHBOARD_LATEST_ADVANCES: `/dashboard/latest-advances/:customer_uuid`,
  DASHBOARD_LATEST_COLLECTIONS: `/dashboard/latest-collections/:customer_uuid`,
  DASHBOARD_SOURCE_EXPOSURE: `/dashboard/source-exposure/:customer_uuid`,
  DASHBOARD_CURRENCY_EXPOSURE: `/dashboard/currency-exposure/:customer_uuid`,
  DASHBOARD_BALANCE_FUNDING: `/dashboard/balance-funding/:customer_uuid`,
  DASHBOARD_TOP_CAMPAIGNS: `/dashboard/campaigns-exposure/:customer_uuid`,
  ACCOUNT_BUSINESS_DETAILS: `/accounts/business-details/:customer_uuid`,
  ACCOUNT_BUSINESS_INFO_UPDATE: `/accounts/business-info/:customer_uuid`,
  ACCOUNT_BUSINESS_ADDRESS_UPDATE: `/accounts/business-address/:customer_uuid`,
  ACCOUNT_COLLECTION_ACCOUNTS: `/accounts/collections/:customer_uuid`,
  ACCOUNT_PAYOUT_ACCOUNTS: `/accounts/payout-accounts/:customer_uuid`,
  ACCOUNT_KYC_STATUS: `/accounts/kyc-status/:customer_uuid`,
  ACCOUNT_CONNECTED_ACCOUNT: `/accounts/connected-account/:customer_uuid`,
  ACCOUNT_KYC_PROCESS: `/accounts/kyc/:customer_uuid`,
  ACCOUNT_PAYOUT_ACCOUNT_CREATE: `/accounts/payout-account/:customer_uuid`,
  ACCOUNT_PAYOUT_ACCOUNT_UPDATE: `/accounts/payout-accounts/:customer_uuid/:payout_account_uuid`,
  ACCOUNT_COLLECTION_DOWNLOAD: `/accounts/collections/:customer_uuid/:collection_uuid/download`,
  ACCOUNT_FINANCIAL_REPORTS: `/accounts/financial-reports/list/:customer_uuid`,
  ACCOUNT_FINANCIAL_REPORTS_CREATE: `/accounts/financial-reports/create/:customer_uuid`,
  ACCOUNT_FINANCIAL_REPORTS_DOWNLOAD: `/accounts/financial-reports/download/:customer_uuid/:ppcr_uuid/:report_id`,
};

// Helper function to get the full endpoint
export function getFullEndpoint(domain: string, endpoint: string): string {
  const baseUrl = getBaseUrl(domain);
  return `${baseUrl}${endpoint}`;
}
